import { useContext, useCallback } from "react";
import { ToastContext } from "./ToastContext";
import type { ToastOptions } from "./types";

export interface UseToastReturn {
  /** Show a toast with custom options */
  toast: (options: ToastOptions) => string;
  /** Show an alert toast */
  alert: (options: Omit<ToastOptions, "type">) => string;
  /** Show a success toast */
  success: (options: Omit<ToastOptions, "type">) => string;
  /** Show a neutral toast */
  neutral: (options: Omit<ToastOptions, "type">) => string;
  /** Show a warning toast */
  warning: (options: Omit<ToastOptions, "type">) => string;
  /** Dismiss a specific toast by ID */
  dismiss: (id: string) => void;
  /** Dismiss all toasts */
  dismissAll: () => void;
}

export const useToast = (): UseToastReturn => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }

  const { addToast, dismissToast, dismissAllToasts } = context;

  const toast = useCallback(
    (options: ToastOptions): string => {
      return addToast(options);
    },
    [addToast]
  );

  const alert = useCallback(
    (options: Omit<ToastOptions, "type">): string => {
      return addToast({ ...options, type: "alert" });
    },
    [addToast]
  );

  const success = useCallback(
    (options: Omit<ToastOptions, "type">): string => {
      return addToast({ ...options, type: "success" });
    },
    [addToast]
  );

  const neutral = useCallback(
    (options: Omit<ToastOptions, "type">): string => {
      return addToast({ ...options, type: "neutral" });
    },
    [addToast]
  );

  const warning = useCallback(
    (options: Omit<ToastOptions, "type">): string => {
      return addToast({ ...options, type: "warning" });
    },
    [addToast]
  );

  const dismiss = useCallback(
    (id: string): void => {
      dismissToast(id);
    },
    [dismissToast]
  );

  const dismissAll = useCallback((): void => {
    dismissAllToasts();
  }, [dismissAllToasts]);

  return {
    toast,
    alert,
    success,
    neutral,
    warning,
    dismiss,
    dismissAll,
  };
};
